/**
 * Route Configuration
 * This file defines all routes in a data-driven format
 * to avoid JSX nesting issues in the main App.js
 */
import { lazy } from 'react';

// Lazy load all page components
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Search = lazy(() => import('@/pages/Search'));
const TransferEntry = lazy(() => import('@/pages/TransferEntry'));
const IssueJobWork  = lazy(() => import('@/pages/IssueJobWork'));
const JobworkReceive = lazy(() => import('@/pages/JobworkReceive'));
const BomChainAudit = lazy(() => import('@/pages/BomChainAudit'));
const StockVsRef = lazy(() => import('@/pages/StockVsRef'));
const Indent = lazy(() => import('@/pages/Indent'));
const IndentPrint = lazy(() => import('@/pages/IndentPrint'));
const IndentGeneral = lazy(() => import('@/pages/IndentGeneral'));
const PPWR = lazy(() => import('@/pages/PPWR'));
const CbamReport = lazy(() => import('@/pages/CbamReport'));
const MyTasks = lazy(() => import('@/pages/MyTasks'));
const TransferHistory = lazy(() => import('@/pages/TransferHistory'));
const ReceiveItem = lazy(() => import('@/pages/ReceiveItem'));
const ReceivedItemsHistory = lazy(() => import('@/pages/ReceivedItemsHistory'));
const SFCodeSummary = lazy(() => import('@/pages/SFCodeSummary'));
const Settings = lazy(() => import('@/pages/Settings'));
const CustomerPortalAdmin = lazy(() => import('@/pages/Settings/CustomerPortalAdmin'));
const UserManagement = lazy(() => import('@/pages/UserManagement'));
const ChangePassword = lazy(() => import('@/pages/ChangePassword'));
const ConsumableIssue = lazy(() => import('@/pages/ConsumableIssue'));
const FinishedProduction = lazy(() => import('@/pages/FinishedProduction'));
const FinishedProductionDashboard = lazy(() => import('@/pages/FinishedProductionDashboard'));
const FinishedProductionHistory = lazy(() => import('@/pages/FinishedProductionHistory'));
const PackingBalance = lazy(() => import('@/pages/PackingBalance'));
const FinishedProductionPlanning = lazy(() => import('@/pages/FinishedProductionPlanning'));
const WipReconciliation = lazy(() => import('@/pages/WipReconciliation'));
const ArticleDatabase = lazy(() => import('@/pages/ArticleDatabase'));
const PackingDatabase = lazy(() => import('@/pages/PackingDatabase'));
const PackingMaterialIssue = lazy(() => import('@/pages/PackingMaterialIssue'));
const PackingDeptAnalysis = lazy(() => import('@/pages/PackingDeptAnalysis'));
const PressShopDeptAnalysis = lazy(() => import('@/pages/PressShopDeptAnalysis'));
const PackingStockShortages = lazy(() => import('@/pages/PackingStockShortages'));
const PalletProduction = lazy(() => import('@/pages/PalletProduction'));
const ContainerLoading = lazy(() => import('@/pages/ContainerLoading'));
const LoadingScan = lazy(() => import('@/pages/LoadingScan'));
const PalletMaster = lazy(() => import('@/pages/Masters/PalletMaster'));
const DieMoldMaster = lazy(() => import('@/pages/Masters/DieMoldMaster'));
const RepairMaintenance = lazy(() => import('@/pages/RepairMaintenance'));
const BackupRestore = lazy(() => import('@/pages/BackupRestore'));

// Production pages
const PressShop = lazy(() => import('@/pages/Production/PressShop'));
const Shearing = lazy(() => import('@/pages/Manufacturing/Shearing'));
const Welding = lazy(() => import('@/pages/Production/Welding'));
const Plating = lazy(() => import('@/pages/Production/Plating'));
const HotDip = lazy(() => import('@/pages/Production/HotDip'));

// Quality Control pages
const Calibration = lazy(() => import('@/pages/Quality/Calibration'));
const Breakdown = lazy(() => import('@/pages/Quality/Breakdown'));
const BreakdownDashboard = lazy(() => import('@/pages/Quality/BreakdownDashboard'));
const ArticleDataReport = lazy(() => import('@/pages/QualityControl/ArticleData'));
const QualityIncomingGoods = lazy(() => import('@/pages/Quality/IncomingGoods'));
const QualityProduction = lazy(() => import('@/pages/Quality/ProductionReports'));
const QualityPreDespatch = lazy(() => import('@/pages/Quality/PreDespatch'));
const QualityWeldingSpecs = lazy(() => import('@/pages/Quality/WeldingSpecs'));
const QualityPressShopSpecs = lazy(() => import('@/pages/Quality/PressShopSpecs'));
const QualitySpecDrift = lazy(() => import('@/pages/Quality/SpecDrift'));

// Material Available — pivot reports over auto_jrc_cache
const MaterialAvailableOItems = lazy(() => import('@/pages/MaterialAvailable/OItems'));
const JrcChange = lazy(() => import('@/pages/JrcChange'));
const MaterialAvailableRmShortage = lazy(() => import('@/pages/MaterialAvailable/RmShortage'));
const SupplierStatus = lazy(() => import('@/pages/SupplierStatus'));
const MaterialAvailableJobworkStatus = lazy(() => import('@/pages/MaterialAvailable/JobworkStatus'));

// Leave Management
const LeaveManagement = lazy(() => import('@/pages/LeaveManagement'));

// JRC Module - Visual Query Builder
const JRCModule = lazy(() => import('@/pages/JRCModule'));
const JRCAdjustment = lazy(() => import('@/pages/JRCAdjustment'));
const JRCTables = lazy(() => import('@/pages/JRCTables'));
const JRCModify = lazy(() => import('@/pages/JRCModify'));
const MaterialIssue = lazy(() => import('@/pages/MaterialIssue'));
const GstItc04 = lazy(() => import('@/pages/GST/GstItc04'));
const JobWorkReport = lazy(() => import('@/pages/GST/JobWorkReport'));
const JRCModifyLog = lazy(() => import('@/pages/JRCModifyLog'));
const JrcExpire = lazy(() => import('@/pages/JrcExpire'));
const DeliveryNotePending = lazy(() => import('@/pages/DeliveryNotePending'));
const BathroomMonitor = lazy(() => import('@/pages/BathroomMonitor'));

// Purchase Posting GST
const PurchasePosting = lazy(() => import('@/pages/PurchasePosting'));

// Freight Cost Management
const FreightCost = lazy(() => import('@/pages/FreightCost'));
const ExportDocs = lazy(() => import('@/pages/ExportDocs'));
const OrderDespatch = lazy(() => import('@/pages/OrderDespatch'));
const PendingPivot = lazy(() => import('@/pages/PendingPivot'));
const CustomerQuery = lazy(() => import('@/pages/CustomerQuery'));

// Master pages
const OperatorMaster = lazy(() => import('@/pages/Masters/OperatorMaster'));
const OperationMaster = lazy(() => import('@/pages/Masters/OperationMaster'));
const MachineMaster = lazy(() => import('@/pages/Masters/MachineMaster'));
const InstrumentMaster = lazy(() => import('@/pages/Masters/InstrumentMaster'));
const ParameterMaster = lazy(() => import('@/pages/master/ParameterMaster'));
const LocationMaster = lazy(() => import('@/pages/Masters/LocationMaster'));
const DepartmentMaster = lazy(() => import('@/pages/Masters/DepartmentMaster'));
const SupplierMaster = lazy(() => import('@/pages/Masters/SupplierMaster'));
const HolidayMaster = lazy(() => import('@/pages/Masters/HolidayMaster'));
const BOMMaster = lazy(() => import('@/pages/Masters/BOMMaster'));
const ArticleImageManager = lazy(() => import('@/pages/Masters/ArticleImageManager'));
const PhotoEnhancer = lazy(() => import('@/pages/Masters/PhotoEnhancer'));

// Stock pages
const LocationStock = lazy(() => import('@/pages/Stock/LocationStock'));
const PackingCost = lazy(() => import('@/pages/Costing/PackingCost'));
const ItemRates = lazy(() => import('@/pages/Costing/ItemRates'));
const ProductCosting = lazy(() => import('@/pages/Costing/ProductCosting'));
const InvoiceMargin = lazy(() => import('@/pages/Costing/InvoiceMargin'));
const BoxLayerMaster = lazy(() => import('@/pages/Costing/BoxLayerMaster'));
const Manpower = lazy(() => import('@/pages/Costing/Manpower'));
const NewItemCosting = lazy(() => import('@/pages/Costing/NewItemCosting'));
const SupplierCostSheets = lazy(() => import('@/pages/Costing/SupplierCostSheets'));

// Shipment pages
const ShipmentPlan = lazy(() => import('@/pages/Shipment/ShipmentPlan'));
const ShipmentReport = lazy(() => import('@/pages/Shipment/ShipmentReport'));
const ErpOrderStatus = lazy(() => import('@/pages/Shipment/ErpOrderStatus'));
const JrcTrace = lazy(() => import('@/pages/Shipment/JrcTrace'));
const BatchSummary = lazy(() => import('@/pages/Shipment/BatchSummary'));
const MyRequirements = lazy(() => import('@/pages/Shipment/MyRequirements'));

// Transfer Rejection page
const TransferRejection = lazy(() => import('@/pages/TransferRejection'));

// Settings pages
const UserPermissions = lazy(() => import('@/pages/Settings/UserPermissions'));

// Admin pages
const DeletionLogs = lazy(() => import('@/pages/Admin/DeletionLogs'));
const EmailReports = lazy(() => import('@/pages/Admin/EmailReports'));
const SecurityDashboard = lazy(() => import('@/pages/Admin/SecurityDashboard'));
const QueryEditor = lazy(() => import('@/pages/Admin/QueryEditor'));

// Utility pages
const Templates = lazy(() => import('@/pages/Templates'));

// SQL Explorer
const SQLExplorer = lazy(() => import('@/pages/SQLExplorer'));

// Report pages
const CoatingReport = lazy(() => import('@/pages/Reports/CoatingReport'));
const ProductionAnalysis = lazy(() => import('@/pages/Reports/ProductionAnalysis'));
const OrderStatus = lazy(() => import('@/pages/Reports/OrderStatus'));
const DepartmentOrderStatus = lazy(() => import('@/pages/Reports/DepartmentOrderStatus'));
const ProductionSalaryReport = lazy(() => import('@/pages/Reports/ProductionSalaryReport'));

// CBAM pages
const EnergyConsumption = lazy(() => import('@/pages/CBAM/EnergyConsumption'));

// Plating Report
const PlatingMachineReport = lazy(() => import('@/pages/Reports/PlatingMachineReport'));

// PPC pages
const MachinePlanning = lazy(() => import('@/pages/PPC/MachinePlanning'));
const CapacityPlanning = lazy(() => import('@/pages/Capacity/CapacityPlanning'));
const ProductionStandards = lazy(() => import('@/pages/PPC/ProductionStandards'));
const ProcessMaster = lazy(() => import('@/pages/PPC/ProcessMaster'));
const MonthlyScheduler = lazy(() => import('@/pages/PPC/MonthlyScheduler'));
const PPCGuide = lazy(() => import('@/pages/PPC/PPCGuide'));
const OrderSchedule = lazy(() => import('@/pages/OrderSchedule'));
const PPCScheduleView = lazy(() => import('@/pages/PPC/PPCScheduleView'));

/**
 * Route configuration array
 * Each route has:
 * - path: URL path
 * - component: Lazy-loaded component
 * - permission: Required permission (null for public routes)
 * - passUser: Whether to pass user prop to component
 * - passHasPermission: Whether to pass hasPermission prop
 * - adminOnly: Requires admin role specifically
 */
export const routeConfig = [
  // Dashboard & Basic
  { path: "/", component: Dashboard, permission: null },
  { path: "search", component: Search, permission: null },
  
  // Transfer/Issue Routes
  { path: "transfer", component: TransferEntry, permission: "entry", passUser: true },
  { path: "receive", component: ReceiveItem, permission: "entry" },
  { path: "history", component: TransferHistory, permission: null, passUser: true, passHasPermission: true },
  { path: "received-history", component: ReceivedItemsHistory, permission: null },
  { path: "issue-job-work", component: IssueJobWork, permission: "entry" },
  { path: "jobwork-receive", component: JobworkReceive, permission: "entry" },
  { path: "bom-chain-audit", component: BomChainAudit, permission: "settings" },
  { path: "stock-vs-ref", component: StockVsRef, permission: "view" },
  { path: "indent", component: Indent, permission: "entry" },
  { path: "indent-general", component: IndentGeneral, permission: "entry" },
  { path: "indent/print/:id", component: IndentPrint, permission: "entry" },
  { path: "ppwr", component: PPWR, permission: "entry" },
  { path: "cbam-report", component: CbamReport, permission: "view" },
  { path: "my-tasks", component: MyTasks, permission: "view" },
  
  // Consumable Issue Routes
  { path: "consumable-issue", component: ConsumableIssue, permission: "entry", passUser: true },
  { path: "consumable-history", component: Dashboard, permission: null },
  
  // Finished Production Routes
  { path: "finished-production", component: FinishedProduction, permission: "entry", passUser: true },
  { path: "finished-dashboard", component: FinishedProductionDashboard, permission: null },
  { path: "finished-history", component: FinishedProductionHistory, permission: null },
  { path: "packing-balance", component: PackingBalance, permission: null },
  { path: "finished-planning", component: FinishedProductionPlanning, permission: null },
  { path: "reports/wip-recon", component: WipReconciliation, permission: null },
  { path: "article-database", component: ArticleDatabase, permission: "view", permissionModule: "article_database" },
  { path: "packing-database", component: PackingDatabase, permission: "view", permissionModule: "packing_database" },
  { path: "packing-material-issue", component: PackingMaterialIssue, permission: "view", permissionModule: "packing_material_issue" },
  { path: "packing-dept-analysis", component: PackingDeptAnalysis, permission: "view", permissionModule: "packing_dept_analysis" },
  { path: "press-shop-dept-analysis", component: PressShopDeptAnalysis, permission: "view", permissionModule: "press_shop_dept_analysis" },
  { path: "packing-stock-shortages", component: PackingStockShortages, permission: "view", permissionModule: "packing_dept_analysis" },
  { path: "pallet-production", component: PalletProduction, permission: "view", permissionModule: "pallet_production" },
  { path: "container-loading", component: ContainerLoading, permission: "view", permissionModule: "pallet_production" },
  { path: "loading-scan", component: LoadingScan, permission: "view", permissionModule: "pallet_production" },
  
  // Other Routes
  { path: "sf-summary", component: SFCodeSummary, permission: null },
  
  // Production Routes
  { path: "production/press-shop", component: PressShop, permission: "view", passUser: true, passHasPermission: true },
  { path: "production/shearing", component: Shearing, permission: "view" },
  { path: "production/welding", component: Welding, permission: "view", passUser: true, passHasPermission: true },
  { path: "production/plating", component: Plating, permission: "view", passUser: true, passHasPermission: true },
  { path: "production/hot-dip", component: HotDip, permission: "view", passUser: true, passHasPermission: true },
  // Hot Dip Reports — convenience routes that render the same module with a
  // pre-selected tab (deep-linked via URL hash to keep routing minimal).
  { path: "reports/hotdip-dehadi", component: HotDip, permission: "view" },
  { path: "reports/hotdip-manpower", component: HotDip, permission: "view" },
  
  // Quality Control Routes
  { path: "quality/article-data", component: ArticleDataReport, permission: null },
  { path: "quality/calibration", component: Calibration, permission: null },
  { path: "quality/breakdown", component: Breakdown, permission: null },
  { path: "quality/breakdown-dashboard", component: BreakdownDashboard, permission: null },
  { path: "quality/incoming-goods", component: QualityIncomingGoods, permission: null },
  { path: "quality/production-reports", component: QualityProduction, permission: null },
  { path: "quality/welding-specs", component: QualityWeldingSpecs, permission: null },
  { path: "quality/press-shop-specs", component: QualityPressShopSpecs, permission: null },
  { path: "quality/pre-despatch", component: QualityPreDespatch, permission: null },
  { path: "quality/spec-drift", component: QualitySpecDrift, permission: null },

  // Material Available — pivot reports over auto_jrc_cache
  { path: "material-available/o-items",       component: MaterialAvailableOItems,       permission: "view" },
  { path: "jrc-change",                       component: JrcChange,                     permission: "settings" },
  { path: "material-available/rm-shortage",    component: MaterialAvailableRmShortage,   permission: "view" },
  { path: "supplier-status",                  component: SupplierStatus,                permission: "view" },
  { path: "material-available/jobwork-status", component: MaterialAvailableJobworkStatus, permission: "view" },
  
  // Master Routes
  { path: "masters/operator", component: OperatorMaster, permission: "settings" },
  { path: "masters/operation", component: OperationMaster, permission: "settings" },
  { path: "masters/machine", component: MachineMaster, permission: "settings" },
  { path: "masters/instrument", component: InstrumentMaster, permission: "settings" },
  { path: "masters/parameters", component: ParameterMaster, permission: "settings" },
  { path: "masters/articles", component: ArticleDatabase, permission: "settings" },
  { path: "masters/locations", component: LocationMaster, permission: "settings" },
  { path: "masters/departments", component: DepartmentMaster, permission: "settings" },
  { path: "masters/suppliers", component: SupplierMaster, permission: "settings" },
  { path: "masters/pallet", component: PalletMaster, permission: "view", permissionModule: "pallet_master" },
  { path: "masters/dies", component: DieMoldMaster, permission: "view", permissionModule: "die_master" },
  { path: "maintenance", component: RepairMaintenance, permission: "view", permissionModule: "maintenance" },
  { path: "masters/holidays", component: HolidayMaster, permission: "settings" },
  { path: "masters/bom", component: BOMMaster, permission: "settings" },
  { path: "masters/article-images", component: ArticleImageManager, permission: "settings" },
  { path: "masters/photo-enhancer", component: PhotoEnhancer, permission: "settings" },
  
  // Stock Routes
  { path: "stock/location", component: LocationStock, permission: "view" },

  // Costing Routes
  { path: "costing/packing", component: PackingCost, permission: "view" },
  { path: "costing/item-rates", component: ItemRates, permission: "view" },
  { path: "costing/product-cost", component: ProductCosting, permission: "view" },
  { path: "costing/invoice-margin", component: InvoiceMargin, permission: "view" },
  { path: "costing/box-layer", component: BoxLayerMaster, permission: "view" },
  { path: "costing/manpower", component: Manpower, permission: "view" },
  { path: "costing/new-item", component: NewItemCosting, permission: "view" },
  { path: "costing/supplier-cost", component: SupplierCostSheets, permission: "view" },
  
  // Templates
  { path: "templates", component: Templates, permission: null },
  
  // PPC Routes
  { path: "ppc/planning", component: MachinePlanning, permission: "view" },
  { path: "ppc/capacity", component: CapacityPlanning, permission: "view" },
  { path: "ppc/standards", component: ProductionStandards, permission: "view" },
  { path: "ppc/process-master", component: ProcessMaster, permission: "view" },
  { path: "ppc/monthly-scheduler", component: MonthlyScheduler, permission: "view" },
  { path: "ppc/guide", component: PPCGuide, permission: "view" },
  { path: "ppc/order-schedule", component: OrderSchedule, permission: "view" },
  { path: "ppc/schedule-view", component: PPCScheduleView, permission: "view" },
  
  // Reports Routes
  { path: "reports/coating", component: CoatingReport, permission: "view" },
  { path: "reports/production-salary", component: ProductionSalaryReport, permission: "view", permissionModule: "production_salary" },
  { path: "reports/production-analysis", component: ProductionAnalysis, permission: "view" },
  { path: "reports/order-status", component: OrderStatus, permission: "view" },
  { path: "reports/department-order-status", component: DepartmentOrderStatus, permission: "view" },
  
  // HR / Leave Management
  { path: "leaves", component: LeaveManagement, permission: "view" },
  
  // JRC Module - Visual Query Builder
  { path: "jrc", component: JRCModule, permission: "view" },
  { path: "jrc/adjustment", component: JRCAdjustment, permission: "view" },
  { path: "jrc/tables", component: JRCTables, permission: "view" },
  { path: "jrc/modify", component: JRCModify, permission: "view" },
  { path: "material-issue", component: MaterialIssue, permission: "view" },
  { path: "gst/itc04", component: GstItc04, permission: "view" },
  { path: "gst/jobwork", component: JobWorkReport, permission: "view" },
  { path: "jrc/modify-log", component: JRCModifyLog, permission: "view" },
  { path: "jrc/expire", component: JrcExpire, permission: "entry" },
  { path: "shipment/dn-pending", component: DeliveryNotePending, permission: "entry" },
  { path: "admin/bathroom-monitor", component: BathroomMonitor, permission: "view" },
  
  // Purchase Posting GST
  { path: "purchase-posting", component: PurchasePosting, permission: "entry" },
  
  // Freight Cost Management
  { path: "freight-cost", component: FreightCost, permission: "entry" },
  { path: "freight-cost/master", component: lazy(() => import("../pages/FreightCostMaster")), permission: "entry" },
  { path: "export-docs", component: ExportDocs, permission: "entry" },
  { path: "order-despatch", component: OrderDespatch, permission: "entry" },
  { path: "pending-pivot", component: PendingPivot, permission: "entry" },
  { path: "customer-query", component: CustomerQuery, permission: "entry" },
  
  // Shipment Routes
  { path: "shipment/plans", component: ShipmentPlan, permission: "entry", passUser: true },
  { path: "shipment/requirements", component: MyRequirements, permission: "view", passUser: true },
  { path: "shipment/report", component: ShipmentReport, permission: "view" },
  { path: "shipment/erp-status", component: ErpOrderStatus, permission: "view" },
  { path: "shipment/jrc-trace", component: JrcTrace, permission: "view" },
  { path: "shipment/batch-summary", component: BatchSummary, permission: "view" },
  
  // Transfer Rejection
  { path: "transfer-rejection", component: TransferRejection, permission: "entry", passUser: true },
  
  // CBAM Module
  { path: "cbam/energy", component: EnergyConsumption, permission: "entry", passUser: true },
  
  // Plating Machine Report
  { path: "reports/plating-machine", component: PlatingMachineReport, permission: "view", passUser: true },
  
  // Settings & Admin
  { path: "backup-restore", component: BackupRestore, permission: "settings" },
  { path: "settings", component: Settings, permission: "settings" },
  { path: "customer-portal", component: CustomerPortalAdmin, permission: "settings" },
  { path: "sql-explorer", component: SQLExplorer, permission: "settings" },
  { path: "users", component: UserManagement, permission: "manage_users" },
  { path: "user-permissions", component: UserPermissions, permission: "manage_users" },
  { path: "admin/deletion-logs", component: DeletionLogs, adminOnly: true },
  { path: "admin/email-reports", component: EmailReports, adminOnly: true },
  { path: "admin/security", component: SecurityDashboard, adminOnly: true },
  { path: "admin/query-editor", component: QueryEditor, permission: "settings" },
  { path: "change-password", component: ChangePassword, permission: null },
];

export default routeConfig;
